<template>
	<div v-show="displayValidationRunning">Running Validation Check...</div>
	<div v-show="!displayValidationRunning" class="relative">
		<!-- Page Label with Selected Option Name -->
		<PageLabel :helpPageName="'Assessments' + props.tabName.replace(/\s+/g, '') + props.subTabName.replace(/\s+/g, '')">
			{{ "Compare" }}
		</PageLabel>

		<div v-if="warningMessage.length > 0">
			<warningBlock :warningMessages="warningMessage" />
		</div>
		<div v-if="optionValidationResults.length > 0">
			<warningBlock header="Warning" :warningMessages="flattenedValidationMessages" />
		</div>

		<!-- Floating toggle pill — always present so the user can hide/show the
		     setup panel at any time. Label + icon reflect the current state. -->
		<button type="button" class="cfg-pill" @click="panelCollapsed = !panelCollapsed">
			<i :class="panelCollapsed ? 'pi pi-sliders-h' : 'pi pi-eye-slash'"></i>
			<span>{{ panelCollapsed ? "Configure" : "Hide options" }}</span>
		</button>

		<!-- Two column layout -->
		<div class="flex flex-row gap-4 mb-4">
			<!-- Left column: Tabs. Grows to full width once the setup panel collapses. -->
			<div class="min-w-0 transition-all duration-300 ease-out" :class="panelCollapsed ? 'flex-1' : 'flex-[3]'">
				<div v-show="resultsAvailable" class="i2Panel">
					<div v-show="displayResult">
						<Tabs value="0">
							<TabList @click="changeTab">
								<Tab value="0">
									<FontAwesomeIcon :icon="['far', 'arrows-spin']" title="Results by Life Cycle Stage" />
									<span class="ml-2">
										{{ props.tabName === tabsDefinitions.packaging.value ? "Results by Life Cycle Stage" : "Results by Life Cycle (Summary)" }}
									</span>
								</Tab>
								<Tab value="1"
									><FontAwesomeIcon
										:icon="props.tabName === tabsDefinitions.packaging.value ? ['far', 'boxes-packing'] : ['far', 'arrows-spin']"
										title="Results by Packaging Type"
									/>
									<span class="ml-2">
										{{ props.tabName === tabsDefinitions.packaging.value ? "Results by Packaging Type" : "Results by Life Cycle (Detailed)" }}
									</span></Tab
								>
								<Tab value="2"
									><FontAwesomeIcon :icon="['far', 'flask-round-potion']" title="Results by Material Composition" /><span class="ml-2"
										>Results by Material Composition</span
									></Tab
								>
								<Tab value="3"
									><FontAwesomeIcon :icon="['far', 'trash-can-list']" title="Results by Consumer waste - End of Life" /><span class="ml-2"
										>Results by Consumer waste - End of Life</span
									></Tab
								>
							</TabList>
							<TabPanels>
								<TabPanel v-for="(tab, tabIndex) in tabConfigs" :key="tabIndex" :value="tabIndex.toString()">
									<h2>{{ tab.title }}</h2>
									<br />
									<div class="grid gap-4" :style="{ gridTemplateColumns: `repeat(${compareResults.length}, 1fr)` }">
										<div v-for="(item, index) in compareResults" :key="index" class="flex flex-col bg-white rounded-lg shadow-sm border p-4">
											<component
												:is="tab.component"
												:results="item.result?.[tab.dataKey]"
												:labelText="item.label"
												:activeEmissionFactor="reportsStore.selectedEmissionFactor ?? undefined"
												:showPercentOfTotal="showPercentOfTotal"
												:labelColumnField="tab.labelColumnField"
												:labelColumnHeaderName="tab.labelColumnHeaderName"
												:grandTotalLabel="tab.grandTotalLabel"
											/>
										</div>
									</div>

									<br />

									<!-- Pie charts -->
									<div v-if="showPieChart" class="grid gap-4" :style="{ gridTemplateColumns: `repeat(${compareResults.length}, 1fr)` }">
										<div v-for="(item, index) in compareResults" :key="index" class="flex flex-col bg-white rounded-lg shadow-sm border p-4">
											<CompareResultsChart
												:finalResult="item.result"
												:labelText="item.label"
												:dataKey="tab.dataKey"
												:showPieChart="true"
												:emissionFactorName="reportsStore.selectedEmissionFactorName ?? undefined"
											/>
										</div>
									</div>

									<!-- Stacked comparison bar -->
									<div v-else class="flex flex-col bg-white rounded-lg shadow-sm border p-4">
										<CompareResultsChart
											:finalResults="compareResults"
											:dataKey="tab.dataKey"
											:showPieChart="false"
											:emissionFactorName="reportsStore.selectedEmissionFactorName ?? undefined"
										/>
									</div>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</div>
				</div>
			</div>

			<!-- Right column: action bar + view settings + compare config.
			     self-start opts out of the row's default align-items:stretch so the
			     column doesn't grow to match the (tall) left column when results are
			     rendered — keeps fields packed at natural content height.
			     The outer wrapper transitions flex-grow/opacity in lockstep with the
			     left column and clips its content via overflow-hidden, so collapsing
			     reads as a single smooth resize rather than a separate slide. The
			     inner div keeps a min-width so its content doesn't reflow/squash
			     while the wrapper shrinks — it's just clipped. -->
			<div
				class="min-w-0 self-start overflow-hidden transition-all duration-300 ease-out"
				:class="panelCollapsed ? 'flex-[0] opacity-0 pointer-events-none' : 'flex-1 opacity-100'"
			>
				<div class="flex flex-col gap-5 mt-3 compare-panel" style="min-width: 320px">
					<!-- Setup card: a refined, editorial config surface. A vertical stepper
				     spine connects the two inputs; the primary action lives in a tinted
				     footer. Accent follows the active theme via var(--primary). -->
					<section class="compare-card compare-card--fade">
						<header class="compare-card__head">
							<span class="compare-card__eyebrow">Configuration</span>
							<h3 class="compare-card__title">Set up comparison</h3>
							<p class="compare-card__subtitle">Choose what to compare, then calculate.</p>
						</header>

						<div class="compare-steps">
							<!-- Step 1: options to compare -->
							<div class="compare-step">
								<div class="compare-step__rail" aria-hidden="true">
									<span class="compare-step__dot">1</span>
									<span class="compare-step__line"></span>
								</div>
								<div class="compare-step__body">
									<div class="compare-step__labelrow">
										<span class="compare-step__label">{{ props.tabName }} options</span>
										<button type="button" class="compare-add" @click="showAddOptionModal = true"><i class="pi pi-plus"></i><span>Add</span></button>
									</div>
									<!-- Selected options as a simple table: name | delete. Adding is
								     done through the "Add" button above (Add Compare Option modal). -->
									<table v-if="selectedCompareOptionsWithLabel.length" class="compare-options-table">
										<tbody>
											<tr v-for="opt in selectedCompareOptionsWithLabel" :key="opt.id">
												<td class="compare-options-table__name">{{ opt.label }}</td>
												<td class="compare-options-table__action">
													<button type="button" class="compare-options-table__remove" title="Remove" @click="removeCompareOption(opt.id)">
														<i class="pi pi-times"></i>
													</button>
												</td>
											</tr>
										</tbody>
									</table>
									<p v-else class="text-xs text-gray-400 italic">No options selected — use Add above.</p>
								</div>
							</div>

							<!-- Step 2: emission factor -->
							<div class="compare-step compare-step--last">
								<div class="compare-step__rail" aria-hidden="true">
									<span class="compare-step__dot">2</span>
								</div>
								<div class="compare-step__body">
									<span class="compare-step__label">Emission factor</span>
									<Select
										v-model="reportsStore.selectedEmissionFactorName"
										:options="reportsStore.emissionFactors"
										optionLabel="emissionFactorName"
										optionValue="emissionFactorName"
										placeholder="Select an emission factor"
										class="w-full mt-1.5"
									/>
								</div>
							</div>
						</div>

						<!-- Footer: primary CTA in a tinted strip so it reads as the climax -->
						<footer class="compare-card__foot">
							<Button
								:label="resultsAvailable ? 'Recalculate' : 'Calculate'"
								icon="pi pi-calculator"
								class="w-full justify-center compare-cta"
								:disabled="isCalculateDisabled || isMissingRequiredFields"
								v-tooltip.top="isMissingRequiredFields ? 'Add at least one option and select an emission factor' : ''"
								@click="loadResults"
							/>
						</footer>
					</section>

					<!-- Display options: only meaningful once results exist, so it is dimmed
				     and inert until there is something to format. -->
					<section class="compare-display compare-card--fade" :class="{ 'compare-display--off': !resultsAvailable }">
						<div class="compare-display__head">
							<span class="compare-card__eyebrow">Display</span>
							<span class="compare-display__hint">applies to results</span>
						</div>
						<div class="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-x-2 gap-y-3.5">
							<!-- Value mode -->
							<span class="compare-toggle__name">Value</span>
							<span></span>
							<span class="compare-toggle__opt" :class="{ 'is-active': !showPercentOfTotal }">Absolute</span>
							<ToggleSwitch v-model="showPercentOfTotal" />
							<span class="compare-toggle__opt" :class="{ 'is-active': showPercentOfTotal }">Percent</span>
							<!-- Chart type -->
							<span class="compare-toggle__name">Chart</span>
							<span></span>
							<span class="compare-toggle__opt" :class="{ 'is-active': !showPieChart }">Bar</span>
							<ToggleSwitch v-model="showPieChart" />
							<span class="compare-toggle__opt" :class="{ 'is-active': showPieChart }">Pie</span>
						</div>
					</section>
				</div>
			</div>
		</div>
	</div>
	<Dialog v-model:visible="showAddOptionModal" modal header="Add Product Format Option" :style="{ width: '600px' }" :closable="false">
		<div class="flex flex-col gap-4">
			<div>
				<I2FormField label="Quantity">
					<InputNumber v-model="tempQuantity" :min="1" :class="{ 'p-invalid': quantityInvalid }" />
				</I2FormField>
				<small v-if="quantityInvalid" class="p-error">Quantity is required</small>
			</div>
			<div>
				<I2FormField label="Format">
					<Select
						v-model="tempFormat"
						:options="resultFormatOptionsWithLabels"
						optionLabel="displayLabel"
						optionValue="id"
						placeholder="Select Format"
						:class="{ 'p-invalid': formatInvalid }"
					></Select>
				</I2FormField>
				<small v-if="formatInvalid" class="p-error">Format is required</small>
			</div>
			<div>
				<I2FormField :label="props.tabName + ' Option'">
					<Select
						v-model="tempGeneralInformationId"
						:options="availableGeneralInformationOptions"
						optionLabel="generalInformationName"
						optionValue="id"
						placeholder="Select Option"
						:class="{ 'p-invalid': generalOptionInvalid }"
					></Select>
					<small v-if="generalOptionInvalid" class="p-invalid">Option is required</small>
				</I2FormField>
			</div>
		</div>

		<template #footer>
			<Button label="Cancel" severity="danger" @click="showAddOptionModal = false" />
			<Button label="Add" severity="primary" @click="addCompareOption" />
		</template>
	</Dialog>
</template>
<script setup lang="ts">
import { ref, onMounted, onActivated, nextTick, watch, computed, inject, type Ref, toRef } from "vue"
import Tabs from "primevue/tabs"
import TabList from "primevue/tablist"
import Tab from "primevue/tab"
import TabPanels from "primevue/tabpanels"
import TabPanel from "primevue/tabpanel"
import Button from "primevue/button"
import InputNumber from "primevue/inputnumber"
import Select from "primevue/select"
import ToggleSwitch from "primevue/toggleswitch"
import {
	ApiClient,
	FileResponse,
	ExportRequest,
	FeatureFlagClient,
	GeneralInformation,
	MasterDropdown,
	ProductFormatClient,
} from "../../entities/NSwagGenerated"
import { EventBus } from "@/eventBus"
import { library } from "@fortawesome/fontawesome-svg-core"
import {
	faInfoCircle,
	faTrash,
	faPen,
	faFloppyDisk,
	faXmarkLarge,
	faArrowsSpin,
	faBoxesPacking,
	faFlaskRoundPotion,
	faTrashCanList,
	faSpinner,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
library.add(faTrash, faPen, faInfoCircle, faFloppyDisk, faXmarkLarge, faArrowsSpin, faBoxesPacking, faFlaskRoundPotion, faTrashCanList, faSpinner)

import { useErrorHandler } from "../composables/useErrorHandler"

import { useTabsDefinitions } from "../composables/tabsDefinitions"
import { useCompareOptionsPersistence, type CompareOption } from "@/composables/useCompareOptionsPersistence"
import { useErrorToaster, useToaster, SystemToastOptions } from "@/composables/toaster"
// SelectableList replaced by an inline options table in the setup panel.
// import SelectableList from "./SelectableList.vue"
import { useRoute } from "vue-router"
import { useReportsStore } from "@/store/reportsStore"
import PageLabel from "./pageLabel.vue"
import warningBlock from "./warningBlock.vue"
import I2FormField from "@/crispify/crispy-components/I2FormField.vue"
import CompareResultsChart from "./compareResultsChart.vue"
import CompareResultsGrid from "./compareResultsGrid.vue"

const showAddOptionModal = ref(false)
const tempGeneralInformationId = ref(null)
const tempQuantity = ref(1)
const tempFormat = ref(null)
const compareOptions = ref<CompareOption[]>([])
const selectedCompareOptionIds = ref<string[]>([])
const compareOptionsRestored = ref(false)
const compareOptionsRestoreReady = ref(false)

const tabsDefinitions = useTabsDefinitions()

const displayResult = ref(true)
const displayValidationRunning = ref(false)
const displayWarning = ref(false)
const warningMessage = ref<string[]>([])

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())

const route = useRoute()
const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	tabName: {
		type: String,
		required: true,
		default: "",
	},
	subTabName: {
		type: String,
		required: false,
		default: "",
	},
	allTabsInformation: {
		type: Object,
		required: true,
		default: "",
	},
})

interface OptionValidationResult {
	optionId: string
	optionName: string
	messages: string[]
}
const optionValidationResults = ref<OptionValidationResult[]>([])

const failingOptionIds = computed(() => new Set(optionValidationResults.value.map((r) => r.optionId)))

const availableGeneralInformationOptions = computed(() => generalInformationOptions.value.filter((o: any) => !failingOptionIds.value.has(o.id)))

const selectedCompareOptionsWithLabel = computed(() =>
	selectedCompareOptions.value.map((option) => {
		const selectedGeneralOption = generalInformationOptions.value.find((generalInformation) => generalInformation.id === option.generalInformationId)
		const selectedFormat = resultFormatOptionsWithLabels.value.find((format) => format.id === option.formatId)

		return {
			...option,
			label: [option.quantity, selectedFormat?.displayLabel + ",", selectedGeneralOption?.generalInformationName].filter(Boolean).join(" "),
			formatLabel: selectedFormat?.displayLabel || "",
		}
	})
)

const selectedCompareOptions = computed(() => compareOptions.value.filter((o) => selectedCompareOptionIds.value.includes(o.id)))

const flattenedValidationMessages = computed(() => [
	`<span style="display:block; font-style:italic; color:#6b7280; font-size:0.875rem; margin-bottom:4px;">Packaging formats with validation errors cannot be used for calculation. Please fix the issues for them if you want to use them for Comparison.</span>`,
	...optionValidationResults.value.flatMap((result) => [
		`<span style="display:block; font-weight:600; font-size:0.9rem; color:#92400e; border-left:2px solid #d97706; padding-left:6px; margin-top:4px;">${result.optionName}</span>`,
		...result.messages.map((msg) => `<span style="display:block; padding-left:16px;">${msg}</span>`),
	]),
])

const compareResults = ref<any[]>([])

const loading = ref(false)
const resultsAvailable = ref(false)
const errorEOL = ref(false)
// Collapses the right-hand setup panel after a successful calculation so the
// results get the full width. The floating "Configure" pill brings it back.
const panelCollapsed = ref(false)
const reportsStore = useReportsStore()
const isWeighted = ref(false)
const weightedOptionEnabled = ref(false)
const generalInformationId = ref<string>()
const selectedOptionName = ref<string>("")
const calculatedOptionName = ref<string>("")
const isMultiOptionFeatureEnabled = ref(false)
let eventDone = 0

// Calculate Results By label for Packaging tab
const calculateResultsByLabel = ref("Reference Unit")
// Reference unit name (e.g., "Tablet", "ml", "Dose") for use in child component titles
const referenceUnitName = ref("")
// Product format name from General Information
const productFormatName = ref("")

// Packaging result options - quantity and format
const resultQuantity = ref<number>(1)
const resultFormat = ref<MasterDropdown | null>(null)
const resultFormatOptions = ref<MasterDropdown[]>([])
const isCalculateDisabled = ref(false)

// Stores the quantity and format label used in the last calculation (set on Calculate/Recalculate)
const calculatedQuantity = ref<number>(1)
const calculatedFormatLabel = ref<string>("")

// GeneralInformation option selector for Packaging/Distribution.
// Injected from commonTabs — read-only, stays in sync automatically when options are
// added/removed/renamed (commonTabs owns the fetch and, for Packaging, the shared store cache).
const injectedGeneralInformationOptions = inject<Ref<GeneralInformation[]>>("packagingOptions", ref([]))
// This tab additionally hides inactive options, which the shared list does not filter out.
const generalInformationOptions = computed<GeneralInformation[]>(() => injectedGeneralInformationOptions.value.filter((gi) => gi.isActive === true))
// Compare keeps its own selection, intentionally independent of the shared "active" selection
// used elsewhere (picking an option to compare against is a local, non-authoritative choice).
const selectedResultGeneralInformationId = ref<string | null>(null)

const showPieChart = ref(false)
const showPercentOfTotal = ref(true)
const submitted = ref(false)

const quantityInvalid = computed(() => submitted.value && !tempQuantity.value)
const formatInvalid = computed(() => submitted.value && !tempFormat.value)
const generalOptionInvalid = computed(() => submitted.value && !tempGeneralInformationId.value)

watch(showAddOptionModal, (value) => {
	if (value) {
		submitted.value = false
	}
})

// Sync sidebar selection when the result dropdown changes
watch(selectedResultGeneralInformationId, async (newId, oldId) => {
	if (!newId || newId === oldId) return
	// Persist selection in DB
	try {
		await apiClient.setGeneralInformationSelected(newId)
	} catch (error) {
		console.error("Error syncing general information selection:", error)
	}
	// Update global tab status
	const tabStatus = props.allTabsInformation?.allTabsStatus?.find((item: any) => item.actualMainTabId === props.tabId)
	if (tabStatus) {
		tabStatus.generalInformationId = newId
	}
})

// Watch quantity to ensure it's always at least 1
watch(resultQuantity, (newValue) => {
	if (newValue === null || newValue === undefined || newValue < 1) {
		resultQuantity.value = 1
	}
})

// Computed to generate format options with dynamic labels and filtering
const resultFormatOptionsWithLabels = computed(() => {
	return resultFormatOptions.value
		.filter((option) => {
			// If Product Format is selected in General Information, hide Reference Unit option
			if (calculateResultsByLabel.value === "Product Format" && option.valueTxt === "Reference Unit") {
				return false
			}
			// If Reference Unit is selected in General Information, hide Product Format option
			if (calculateResultsByLabel.value === "Reference Unit" && option.valueTxt === "Product Format") {
				return false
			}
			return true
		})
		.map((option) => {
			let label = option.valueTxt || ""
			// Add unit name in brackets for Product Format and Reference Unit
			if (option.valueTxt === "Product Format" && productFormatName.value) {
				label = `Product Format [${productFormatName.value}]`
			} else if (option.valueTxt === "Reference Unit" && referenceUnitName.value) {
				label = `Reference Unit [${referenceUnitName.value}]`
			}
			return {
				...option,
				displayLabel: label,
			}
		})
})
const { saveCompareOptions, restoreCompareOptions } = useCompareOptionsPersistence({
	assessmentId: toRef(props, "assessmentId"),
	tabId: toRef(props, "tabId"),
	compareOptions,
	selectedCompareOptionIds,
	generalInformationOptions,
	resultFormatOptionsWithLabels,
	restoreState: compareOptionsRestored,
	canRestore: compareOptionsRestoreReady,
})

// Load result format options from database
const loadResultFormatOptions = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value) {
		return
	}

	try {
		const options = await apiClient.masterDropdownByCategoryKey("resultFormatOption")
		// Sort by ordering field
		options.sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0))
		resultFormatOptions.value = options
	} catch (error) {
		console.error("Error loading result format options:", error)
	}
}

// Set the default format selection to first available filtered option
const setDefaultResultFormat = () => {
	const filteredOptions = resultFormatOptionsWithLabels.value
	if (filteredOptions.length === 0) return

	// Check if current selection is still valid in the filtered options
	const currentSelection = resultFormat.value
	const isCurrentSelectionValid = currentSelection && filteredOptions.some((opt) => opt.id === currentSelection.id)

	// If no selection or current selection is not valid, set to first available
	if (!currentSelection || !isCurrentSelectionValid) {
		resultFormat.value = filteredOptions[0] || null
	}
}

// Watch for changes in calculateResultsByLabel to update the selection when it changes
watch(calculateResultsByLabel, () => {
	setDefaultResultFormat()
})
// added to watch compareOptions and save to local storage whenever it changes
watch(compareOptions, saveCompareOptions, { deep: true })

// Load selected option name from general information
const loadSelectedOptionName = async (giId: string) => {
	try {
		// Load all generalInformation options for this assessment/screen and find the one with matching ID
		const options = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)
		const gi = options.find((opt) => opt.id === giId)
		if (gi) {
			selectedOptionName.value = gi.generalInformationName || gi.versionName || ""
		}
	} catch (error) {
		console.error("Error loading general information name:", error)
		selectedOptionName.value = ""
	}
}

// Load Calculate Results By type from General Information for Packaging tab
const loadCalculateResultsByType = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value) {
		return
	}

	try {
		const result = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)
		if (result && result.length > 0) {
			const packagingGI = result[0]
			if (packagingGI) {
				// If packReferenceUnit has a value, it's "Reference Unit"
				// If productFormatId has a value, it's "Product Format"
				if (packagingGI.packReferenceUnit != null && packagingGI.packReferenceUnit.id != null) {
					calculateResultsByLabel.value = "Reference Unit"
					// Store the unit name for child components
					referenceUnitName.value = packagingGI.packReferenceUnit.valueTxt || ""
					productFormatName.value = ""
				} else if (packagingGI.productFormatId != null && packagingGI.productFormatId !== "") {
					calculateResultsByLabel.value = "Product Format"
					referenceUnitName.value = ""
					// Load the product format name
					try {
						const productFormatClient = new ProductFormatClient(import.meta.env.VITE_API_URL?.toString())
						const productFormats = await productFormatClient.list()
						const productFormat = productFormats.find((pf) => pf.id === packagingGI.productFormatId)
						productFormatName.value = productFormat?.formatName || ""
					} catch {
						productFormatName.value = ""
					}
				}
			}
		}
	} catch (error) {
		// Default to Reference Unit if there's an error
		calculateResultsByLabel.value = "Reference Unit"
		referenceUnitName.value = ""
		productFormatName.value = ""
	}
}

// Auto-select the tracked generalInformationId (or wasSelected, or fall back to the first) for
// the Option dropdown (Packaging/Distribution). This tab no longer fetches its own copy of the
// options — it injects the list from commonTabs — so the selection is derived reactively
// (immediately on mount, and again whenever commonTabs updates the shared list, the feature flag
// resolves, or the tracked generalInformationId changes) instead of being tied to a fetch completing.
const selectDefaultGeneralInformationOption = () => {
	if (props.tabName !== tabsDefinitions.packaging.value && props.tabName !== tabsDefinitions.distribution.value) {
		return
	}
	if (!isMultiOptionFeatureEnabled.value) {
		return
	}

	const options = generalInformationOptions.value
	if (options.length === 0) return

	// Pre-select: use the current generalInformationId, or find wasSelected, or fall back to first
	if (generalInformationId.value && options.some((gi) => gi.id === generalInformationId.value)) {
		selectedResultGeneralInformationId.value = generalInformationId.value
	} else {
		const selected = options.find((gi) => gi.wasSelected === true) || options[0]
		selectedResultGeneralInformationId.value = selected?.id ?? null
	}
}
watch([generalInformationOptions, isMultiOptionFeatureEnabled, generalInformationId], selectDefaultGeneralInformationOption, { immediate: true })

// Check EOL destination setting for packaging tab
const checkEolDestinationSetting = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value) {
		return
	}

	try {
		// Get the current packaging generalInformationId if available
		const packagingGiId =
			generalInformationId.value ||
			props.allTabsInformation?.allTabsStatus?.find((item: any) => item.actualMainTabId === props.tabId)?.generalInformationId

		// Pass packagingGeneralInformationId to check the correct Consumer End Of Life record
		const result = await apiClient.checkIfDestinationMarketSelected(props.assessmentId, packagingGiId || undefined)

		// If successful, remove any previous EOL warning and enable Calculate button
		warningMessage.value = warningMessage.value.filter((msg) => !msg.includes("Destination") && !msg.includes("Consumer End Of Life"))
		isCalculateDisabled.value = false
		displayWarning.value = warningMessage.value.length > 0
	} catch (err: any) {
		console.log("checkEolDestinationSetting: API returned error", err)
		console.log("checkEolDestinationSetting: err.result", err?.result)
		// If error, add warning message and disable Calculate button
		// NSwag stores the response body in err.result for 400 errors
		const errorMessage =
			err?.result?.message ||
			err?.response?.message ||
			"Please open 'Consumer End Of Life' tab and select the Destination Setting field, then click Save"
		// Check if this warning is not already present
		if (!warningMessage.value.some((msg) => msg.includes("Destination") || msg.includes("Consumer End Of Life"))) {
			warningMessage.value.push(errorMessage)
		}
		displayResult.value = false
		displayWarning.value = true
		isCalculateDisabled.value = true
	}
}

onMounted(async () => {
	// Check if weighted calculation option is enabled
	const weightedFlag = await featureFlagClient.getByName("Weighted Calculation Option")
	weightedOptionEnabled.value = weightedFlag?.isEnabled || false

	// Check if multi-option feature flag is enabled
	try {
		const multiOptionFlag = await featureFlagClient.getByName("Allow Multiple Packaging Options per Assessment")
		isMultiOptionFeatureEnabled.value = multiOptionFlag?.isEnabled || false
	} catch (error) {
		isMultiOptionFeatureEnabled.value = false
	}
	// Load result format options for Packaging tab
	await loadResultFormatOptions()

	// Load Calculate Results By type for Packaging tab
	await loadCalculateResultsByType()

	// Set default format selection after both options and type are loaded
	setDefaultResultFormat()
	compareOptionsRestoreReady.value = true
	restoreCompareOptions()

	// Check EOL destination setting for packaging tab
	await checkEolDestinationSetting()

	// Event listener for result validation triggered from parent
	EventBus.on("validateResult_" + props.tabId, async (data: any) => {
		optionValidationResults.value = []

		if (data != null && data.generalInformationId != null) {
			// Update tracked generalInformationId and load selected option name if changed
			const giChanged = generalInformationId.value !== data.generalInformationId
			if (giChanged || !selectedOptionName.value) {
				await loadSelectedOptionName(data.generalInformationId)
			}
			generalInformationId.value = data.generalInformationId
		}

		displayValidationRunning.value = true
		// Re-load Calculate Results By type when navigating to this tab
		await loadCalculateResultsByType()
		// Update default format selection
		setDefaultResultFormat()
		// Re-check EOL destination setting
		await checkEolDestinationSetting()
		// Validate all packaging options in parallel
		await validateAllPackagingOptions()
	})

	// Event listener to force reset results (e.g., when GeneralInformation option changes)
	EventBus.on("forceResetResults_" + props.tabId, () => {
		resultsAvailable.value = false
		displayResult.value = false

		// Clear ALL comparison results
		compareResults.value = []
		optionValidationResults.value = []
	})
})

// Re-check EOL destination setting when component is reactivated (keep-alive)
onActivated(async () => {
	// Re-load Calculate Results By type when component is reactivated
	await loadCalculateResultsByType()
	// Update default format selection
	setDefaultResultFormat()
	await checkEolDestinationSetting()
})

const status = ref()

const validateAllPackagingOptions = async () => {
	if (!generalInformationOptions.value.length) return
	displayValidationRunning.value = true
	optionValidationResults.value = []

	const results: OptionValidationResult[] = await Promise.all(
		generalInformationOptions.value.map(async (option: any): Promise<OptionValidationResult> => {
			const name = option.generalInformationName || option.versionName || "Unknown Option"
			try {
				const res = await apiClient.validateResultMatrixByGeneralInformationId(option.id)
				const messages = res?.map((item) => item.messageText!).filter(Boolean) ?? []
				return { optionId: option.id, optionName: name, messages }
			} catch (err: any) {
				return { optionId: option.id, optionName: name, messages: [err?.response?.message || "Validation failed"] }
			}
		})
	)

	optionValidationResults.value = results.filter((r) => r.messages.length > 0)
	displayValidationRunning.value = false
}

// Populate process waste tables before calculating results
async function populateProcessWaste(): Promise<void> {
	const actualTab = props.allTabsInformation?.allTabsStatus?.find((item: any) => item.actualMainTabId === props.tabId)

	if (!actualTab) return

	const genInfoId = actualTab.generalInformationId
	const stepId = actualTab.selectedStepId

	// Populate processWaste for the current step
	if (genInfoId && stepId) {
		try {
			await apiClient.populateProcessWasteAndTable(
				props.assessmentId,
				genInfoId,
				props.tabId,
				stepId,
				"actual", // Load actual saved data
				undefined, // region - only needed for EOL
				undefined // selectedPackagingGeneralInformationId - Consumer End of Life only
			)
		} catch (err) {
			console.warn("Failed to populate process waste, continuing with calculation:", err)
			// Do not block calculation if population fails
		}
	}
}

const loadResults = async () => {
	// Calculate button simply loads results without validation
	// Validation already happened on page load via EventBus
	resultsAvailable.value = false
	loading.value = true
	displayResult.value = true

	const actualTab = props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
	if (props.subTabName.replace(/\s+/g, "") === actualTab?.actualSubTabName && route.query.assessmentId === props.assessmentId) {
		eventDone++
		// For packaging tab, pass quantity and format type parameters
		const quantity = props.tabName === tabsDefinitions.packaging.value ? resultQuantity.value : undefined

		try {
			// Populate process waste tables before calculating results
			await populateProcessWaste()

			// Then calculate results - pass selectedResultGeneralInformationId if available
			const giIdParam = selectedResultGeneralInformationId.value || undefined

			compareResults.value = []

			const emissionFactors = reportsStore.selectedEmissionFactorName ? [reportsStore.selectedEmissionFactorName] : undefined

			for (const option of selectedCompareOptionsWithLabel.value) {
				const result = await apiClient.getPackResults(
					props.assessmentId,
					props.tabName,
					emissionFactors,
					isWeighted.value,
					option.quantity,
					option.formatLabel,
					option.generalInformationId
				)
				compareResults.value.push({
					label: option.label,
					result,
					quantity: option.quantity,
				})
			}

			resultsAvailable.value = true
			loading.value = false
			errorEOL.value = false
			// Auto-collapse the setup panel to hand the full width to the results.
			panelCollapsed.value = true

			// Capture the quantity and format used for this calculation
			calculatedQuantity.value = quantity ?? 1
			const matchedOption = resultFormatOptionsWithLabels.value.find((opt) => opt.id === resultFormat.value?.id)
			calculatedFormatLabel.value = matchedOption?.displayLabel || resultFormat.value?.valueTxt || calculateResultsByLabel.value || ""

			// Store the name of the generalInformation used for this calculation
			if (isMultiOptionFeatureEnabled.value && giIdParam) {
				const option = generalInformationOptions.value.find((gi) => gi.id === giIdParam)
				calculatedOptionName.value = option?.generalInformationName || ""
			} else {
				calculatedOptionName.value = ""
			}
		} catch (err: any) {
			useErrorHandler().setErrorHandled(err.response)
			let msg: SystemToastOptions = {
				severity: "warn",
				summary: "Warning",
				detail: err.response?.message || err.message,
				life: 5000,
			}

			useToaster(msg)
			errorEOL.value = err.response?.message?.toLowerCase().includes("destination market")
			loading.value = false
			resultsAvailable.value = false
		}
	}
}

watch(
	isWeighted,
	() => {
		if (displayResult.value) {
			loadResults()
		}
	},
	{ flush: "post" }
)

async function changeTab() {
	await nextTick()
	// Chart data is now loaded when user manually selects impact category
}

function addCompareOption() {
	submitted.value = true

	if (quantityInvalid.value || formatInvalid.value || generalOptionInvalid.value) {
		return
	}

	const selectedFormat = resultFormatOptionsWithLabels.value.find((f) => f.id === tempFormat.value)
	const newId = Date.now().toString()

	compareOptions.value.push({
		id: newId,
		generalInformationId: tempGeneralInformationId.value,
		quantity: tempQuantity.value,
		formatId: tempFormat.value,
	})

	selectedCompareOptionIds.value.push(newId)

	// reset fields
	tempGeneralInformationId.value = null
	tempQuantity.value = 1
	tempFormat.value = null

	showAddOptionModal.value = false
}
//added to remove a compare option from the list and update the selected IDs
function removeCompareOption(optionId: string) {
	compareOptions.value = compareOptions.value.filter((option) => option.id !== optionId)
	selectedCompareOptionIds.value = selectedCompareOptionIds.value.filter((id) => id !== optionId)
}

const tabConfigs = [
	{
		title: "Life Cycle Comparison",
		labelColumnField: "stageName",
		labelColumnHeaderName: "Life Cycle Stage",
		dataKey: "resultByLifeCycleStage",
		component: CompareResultsGrid,
		grandTotalLabel: "Grand Total",
	},
	{
		title: "Packaging Type Comparison",
		labelColumnField: "stepName",
		labelColumnHeaderName: "Packaging Type",
		dataKey: "resultByPackagingType",
		component: CompareResultsGrid,
		grandTotalLabel: "Subtotal [Material Production]",
	},
	{
		title: "Material Composition Comparison",
		labelColumnField: "materialName",
		labelColumnHeaderName: "Material",
		dataKey: "resultByMaterialComposition",
		component: CompareResultsGrid,
		grandTotalLabel: "Subtotal [Material Production]",
	},
	{
		title: "Process Waste Comparison",
		labelColumnField: "materialName",
		labelColumnHeaderName: "Waste Type",
		dataKey: "resultByEol",
		component: CompareResultsGrid,
		grandTotalLabel: "Subtotal [Consumer Waste]",
	},
]

const isMissingRequiredFields = computed(() => {
	return !selectedCompareOptionIds.value.length || !(reportsStore.selectedEmissionFactorNames?.length > 0)
})
</script>

<style scoped>
/* Material Compare — right-hand configuration panel.
   Accent is driven by the active theme's --primary so it restyles per-tenant.
   Falls back to a calm slate-blue when the token is absent. */
.compare-panel {
	--cmp-accent: var(--primary, #2f5f6b);
	--cmp-ink: #1f2937;
	--cmp-muted: #6b7280;
	--cmp-line: #e7eaee;
	--cmp-surface: #ffffff;
}

/* Card shell: soft ring + layered shadow for quiet depth (no harsh borders). */
.compare-card {
	background: var(--cmp-surface);
	border-radius: 14px;
	box-shadow:
		0 1px 2px rgba(16, 24, 40, 0.04),
		0 8px 24px -12px rgba(16, 24, 40, 0.18);
	border: 1px solid var(--cmp-line);
	overflow: hidden;
}

.compare-card__head {
	padding: 1rem 1.15rem 0.85rem;
	background: var(--cmp-surface);
	border-bottom: 1px solid var(--cmp-line);
}
.compare-card__eyebrow {
	display: block;
	font-size: 0.625rem;
	font-weight: 700;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: var(--cmp-accent);
	opacity: 0.85;
}
.compare-card__title {
	margin: 0.15rem 0 0;
	font-size: 1.05rem;
	font-weight: 650;
	letter-spacing: -0.01em;
	color: var(--cmp-ink);
}
.compare-card__subtitle {
	margin: 0.15rem 0 0;
	font-size: 0.75rem;
	color: var(--cmp-muted);
}

/* Stepper: a single spine connects the numbered dots. */
.compare-steps {
	padding: 1.1rem 1.15rem 0.4rem;
}
.compare-step {
	display: grid;
	grid-template-columns: 1.6rem 1fr;
	column-gap: 0.85rem;
	padding-bottom: 1.15rem;
}
.compare-step__rail {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
}
.compare-step__dot {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 1.6rem;
	height: 1.6rem;
	border-radius: 999px;
	font-size: 0.72rem;
	font-weight: 700;
	color: var(--cmp-accent);
	background: #eef2f7;
	border: 1px solid var(--cmp-line);
	flex: 0 0 auto;
	z-index: 1;
}
/* Connector line down to the next step's dot. */
.compare-step__line {
	flex: 1 1 auto;
	width: 2px;
	margin-top: 2px;
	background: var(--cmp-line);
	border-radius: 2px;
}
.compare-step--last {
	padding-bottom: 0.2rem;
}
.compare-step__body {
	min-width: 0;
	padding-top: 0.1rem;
}
.compare-step__labelrow {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
	margin-bottom: 0.5rem;
}
.compare-step__label {
	font-size: 0.82rem;
	font-weight: 600;
	color: var(--cmp-ink);
}

/* "Add" affordance: quiet text link that warms on hover. */
.compare-add {
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	font-size: 0.74rem;
	font-weight: 600;
	color: var(--cmp-accent);
	padding: 0.2rem 0.45rem;
	border-radius: 7px;
	transition:
		background 0.15s ease,
		transform 0.15s ease;
}
.compare-add:hover {
	background: color-mix(in srgb, var(--cmp-accent) 10%, white);
}
.compare-add:active {
	transform: scale(0.96);
}
.compare-add .pi {
	font-size: 0.68rem;
}

/* Selected-options table — name | delete. */
.compare-options-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.82rem;
}
.compare-options-table td {
	border-bottom: 1px solid var(--cmp-line, #e7eaee);
	padding: 0.45rem 0.5rem;
	vertical-align: middle;
}
.compare-options-table tr:last-child td {
	border-bottom: none;
}
.compare-options-table__name {
	color: var(--cmp-ink, #1f2937);
	word-break: break-word;
}
.compare-options-table__action {
	width: 2.2rem;
	text-align: right;
}
.compare-options-table__remove {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 1.6rem;
	height: 1.6rem;
	border-radius: 7px;
	color: #b91c1c;
	transition:
		background 0.15s ease,
		transform 0.15s ease;
}
.compare-options-table__remove:hover {
	background: #fee2e2;
}
.compare-options-table__remove:active {
	transform: scale(0.94);
}
.compare-options-table__remove .pi {
	font-size: 0.78rem;
}

/* Footer CTA strip. */
.compare-card__foot {
	padding: 0.9rem 1.15rem 1.1rem;
	background: var(--cmp-surface);
	border-top: 1px solid var(--cmp-line);
}
.compare-cta {
	font-weight: 650;
	letter-spacing: 0.01em;
	box-shadow: 0 6px 16px -8px color-mix(in srgb, var(--cmp-accent) 60%, transparent);
}

/* Display card. */
.compare-display {
	background: var(--cmp-surface);
	border: 1px solid var(--cmp-line);
	border-radius: 14px;
	padding: 1rem 1.15rem 1.1rem;
	display: flex;
	flex-direction: column;
	gap: 0.85rem;
	transition:
		opacity 0.25s ease,
		filter 0.25s ease;
}
.compare-display--off {
	opacity: 0.5;
	filter: grayscale(0.3);
	pointer-events: none;
}
.compare-display__head {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
}
.compare-display__hint {
	font-size: 0.68rem;
	color: #9aa3af;
	font-style: italic;
}
.compare-toggle__name {
	font-size: 0.82rem;
	font-weight: 600;
	color: var(--cmp-ink);
}
.compare-toggle__opt {
	font-size: 0.72rem;
	color: var(--cmp-muted);
	text-align: right;
	transition:
		color 0.15s ease,
		font-weight 0.15s ease;
}
.compare-toggle__opt.is-active {
	color: var(--cmp-accent);
	font-weight: 700;
}
/* the right-hand option of each pair is left-aligned */
.compare-toggle__opt:nth-of-type(even) {
	text-align: left;
}

/* Staggered entrance — one orchestrated reveal beats scattered micro-anims. */
.compare-card--fade {
	animation: comparePanelIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.compare-display.compare-card--fade {
	animation-delay: 0.08s;
}
@keyframes comparePanelIn {
	from {
		opacity: 0;
		transform: translateY(8px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
@media (prefers-reduced-motion: reduce) {
	.compare-card--fade {
		animation: none;
	}
}

/* Floating "Configure" pill — reopens the collapsed setup panel.
   Pinned top-right of the tab area, accent-tinted, lifts on hover. */
.cfg-pill {
	position: absolute;
	top: 0.35rem;
	right: 0.5rem;
	z-index: 20;
	display: inline-flex;
	align-items: center;
	gap: 0.45rem;
	padding: 0.45rem 0.9rem;
	font-size: 0.78rem;
	font-weight: 650;
	color: var(--primary, #2f5f6b);
	background: #ffffff;
	border: 1px solid #e7eaee;
	border-radius: 999px;
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
	cursor: pointer;
	transition:
		transform 0.15s ease,
		background 0.15s ease;
}
.cfg-pill:hover {
	background: #f3f4f6;
	transform: translateY(-1px);
}
.cfg-pill:active {
	transform: translateY(0) scale(0.97);
}
.cfg-pill .pi {
	font-size: 0.82rem;
}

/* Pill enter/leave */
.cfg-pill-enter-active,
.cfg-pill-leave-active {
	transition:
		opacity 0.2s ease,
		transform 0.2s ease;
}
.cfg-pill-enter-from,
.cfg-pill-leave-to {
	opacity: 0;
	transform: translateY(-6px) scale(0.95);
}

@media (prefers-reduced-motion: reduce) {
	.cfg-pill-enter-active,
	.cfg-pill-leave-active {
		transition: none;
	}
}
</style>
